import React, { useEffect, useRef } from "react";
import { useChatContext } from "../../contexts/ChatContext";
import { useTasksContext } from "../../contexts/TasksContext";
import { TaskStatus, TaskType } from "../../types/taskTypes";

/**
 * Si une recherche exhaustive (job serveur) est encore en cours et qu’on rouvre le fil
 * sans SSE actif, rattache le flux via POST /chat/exhaustive-search/resume (même endpoint pour tous les modes).
 */
export function ExhaustiveSearchStreamResume({ page }: { page: string }) {
  const {
    selectedThread,
    resumeExhaustiveSearchStream,
    isLoadingQuery,
    fetchMessages,
  } =
    useChatContext();
  const { tasks } = useTasksContext();
  const prevThreadIdRef = useRef<string | undefined>(undefined);
  const resumeAttemptedRef = useRef(false);
  const lastKnownStatusRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const tid = selectedThread?.id;
    if (tid !== prevThreadIdRef.current) {
      prevThreadIdRef.current = tid;
      resumeAttemptedRef.current = false;
    }
  }, [selectedThread?.id]);

  useEffect(() => {
    if (page === "home") return;
    const tid = selectedThread?.id;
    if (!tid || isLoadingQuery) return;
    if (resumeAttemptedRef.current) return;

    const active = tasks.some(
      (t) =>
        t.type === TaskType.LEGAL_SEARCH &&
        t.threadId === tid &&
        t.requiresServerResume === true &&
        (t.status === TaskStatus.PROCESSING ||
          t.status === TaskStatus.SUMMARIZING)
    );
    if (!active) return;

    resumeAttemptedRef.current = true;
    void resumeExhaustiveSearchStream(tid);
  }, [
    page,
    selectedThread?.id,
    tasks,
    isLoadingQuery,
    resumeExhaustiveSearchStream,
  ]);

  useEffect(() => {
    const tid = selectedThread?.id;
    if (!tid) return;
    const t = tasks.find(
      (x) => x.type === TaskType.LEGAL_SEARCH && x.threadId === tid
    );
    if (!t) return;
    if (t.requiresServerResume !== true) return;

    const prev = lastKnownStatusRef.current[tid];
    const next = t.status;
    if (prev !== next) {
      lastKnownStatusRef.current = { ...lastKnownStatusRef.current, [tid]: next };
    }
    if (
      (prev === TaskStatus.PROCESSING || prev === TaskStatus.SUMMARIZING) &&
      (next === TaskStatus.COMPLETED ||
        next === TaskStatus.CANCELLED ||
        next === TaskStatus.ERROR)
    ) {
      // Quand le job serveur est terminé, rafraîchir automatiquement l'historique du fil
      // pour afficher la réponse/carousel sans action manuelle.
      fetchMessages(tid);
    }
  }, [selectedThread?.id, tasks, fetchMessages]);

  return null;
}
